<template>
  <v-card style="padding: 30px 30px 50px 30px">
    <mf-funnel-graph
      full-width
      :labels="labels"
      :label-values="labelValues"
      :percentage-values="percentageValues"
      :sub-labels="subLabels"
      :tooltip-revenue="tooltipRevenue"
      :tooltip-percentage="tooltipPercentage"
      :label-revenues="labelRevenues"
      :height="height"
      :width="width"
      :data-set-num="dataSetNum"
      :sub-label-value="subLabelValue"
      :values="funnelChartData"
      :colors="colors"
      :direction="direction"
      :loading="loading"
      :gradient-direction="gradientDirection"
    >
      <template v-slot:quick-action="{ index }">
        <mf-quick-actions v-if="quickActionsProps[index]" v-bind="quickActionsProps[index]" />
      </template>
    </mf-funnel-graph>
  </v-card>
</template>

<script>
import {
  criteriaGroup,
  campaignReceivedCriteria,
  campaignReceivedAndPurchasedAtUnitsOnFixedPeriodCriteria,
  campaignReceivedAndPurchasedOfferProductAtUnitsCriteria,
  campaignReceivedAndPurchasedVariableProductAtStoreCriteria,
} from '@/components/quick-actions/segmentationGroups.js'
import { mariaResults } from '@/modules/maria/campanha-da-maria-v2/mixins/maria-results'

export default {
  name: 'CustomersTypesFunnelGraph',

  mixins: [mariaResults],

  props: {
    metrics: {
      type: Object,
      required: true,
    },
    dateCriteriaFilter: {
      type: Object,
      default: () => {},
    },
    channels: {
      type: Array,
      default: () => {},
    },
    units: {
      type: Array,
      default: () => {},
    },
    unitsConfiguration: {
      type: Array,
      default: () => {},
    },
  },

  data: () => ({
    labels: [
      'Clientes receberam',
      'Clientes receberam e compraram',
      'Clientes receberam e compraram os produtos da(s) oferta(s) divulgados',
      'Clientes receberam e compraram o produto que receberam via variável na mensagem',
    ],
    tooltipRevenue: 'Faturamento que esses clientes geraram',
    tooltipPercentage: 'Porcentagem dos clientes que receberam',
    height: 300,
    width: 800,
    dataSetNum: 1,
    subLabelValue: 'percent',
    direction: 'horizontal',
    gradientDirection: 'horizontal',
    colors: [
      ['#209A7A', '#3DD88C'],
      ['#EAF9E8', '#B9EBCF'],
      ['#09535C', '#158372'],
      ['#17A08A', '#2BDC87'],
    ],
  }),

  computed: {
    subLabels() {
      return [
        ...(!this.isChannelActivated('sms') ? [] : ['Receberam SMS']),
        ...(!this.isChannelActivated('email') ? [] : ['Receberam E-mail']),
        ...(!this.isChannelActivated('whatsapp') ? [] : ['Receberam Whatsapp']),
        ...(!this.isChannelActivated('push') ? [] : ['Recebem push aplicativo']),
      ]
    },
    labelValues() {
      return [
        this.metrics?.clients_metrics?.general?.received?.count,
        this.metrics?.clients_metrics?.general?.received_and_bought?.count,
        this.metrics?.clients_metrics?.general?.received_and_bought_with_offer_products?.count,
        this.metrics?.clients_metrics?.general?.received_and_bought_with_received_product?.count,
      ]
    },
    percentageValues() {
      const maxValue = Math.max(...this.labelValues)
      const roundPoint = (number) => Math.round(number * 10) / 10

      return this.labelValues.map((value) => (value === 0 ? 0 : roundPoint((value * 100) / maxValue)))
    },
    labelRevenues() {
      return [
        this.metrics?.clients_metrics?.general?.received?.revenue,
        this.metrics?.clients_metrics?.general?.received_and_bought?.revenue,
        this.metrics?.clients_metrics?.general?.received_and_bought_with_offer_products?.revenue,
        this.metrics?.clients_metrics?.general?.received_and_bought_with_received_product?.revenue,
      ]
    },
    funnelChartData() {
      return [
        [
          ...(!this.isChannelActivated('sms') ? [] : [this.metrics?.clients_metrics?.channels?.sms.received?.count]),
          ...(!this.isChannelActivated('email') ? [] : [this.metrics?.clients_metrics?.channels?.email.received?.count]),
          ...(!this.isChannelActivated('whatsapp') ? [] : [this.metrics?.clients_metrics?.channels?.whatsapp.received?.count]),
          ...(!this.isChannelActivated('push') ? [] : [this.metrics?.clients_metrics?.channels?.push.received?.count]),
        ],
        [
          ...(!this.isChannelActivated('sms') ? [] : [this.metrics?.clients_metrics?.channels?.sms.received_and_bought?.count]),
          ...(!this.isChannelActivated('email') ? [] : [this.metrics?.clients_metrics?.channels?.email.received_and_bought?.count]),
          ...(!this.isChannelActivated('whatsapp') ? [] : [this.metrics?.clients_metrics?.channels?.whatsapp.received_and_bought?.count]),
          ...(!this.isChannelActivated('push') ? [] : [this.metrics?.clients_metrics?.channels?.push.received_and_bought?.count]),
        ],
        [
          ...(!this.isChannelActivated('sms') ? [] : [this.metrics?.clients_metrics?.channels?.sms.received_and_bought_with_offer_products?.count]),
          ...(!this.isChannelActivated('email') ? [] : [this.metrics?.clients_metrics?.channels?.email.received_and_bought_with_offer_products?.count]),
          ...(!this.isChannelActivated('whatsapp') ? [] : [this.metrics?.clients_metrics?.channels?.whatsapp.received_and_bought_with_offer_products?.count]),
          ...(!this.isChannelActivated('push') ? [] : [this.metrics?.clients_metrics?.channels?.push.received_and_bought_with_offer_products?.count]),
        ],
        [
          ...(!this.isChannelActivated('sms') ? [] : [this.metrics?.clients_metrics?.channels?.sms.received_and_bought_with_received_product?.count]),
          ...(!this.isChannelActivated('email') ? [] : [this.metrics?.clients_metrics?.channels?.email.received_and_bought_with_received_product?.count]),
          ...(!this.isChannelActivated('whatsapp') ? [] : [this.metrics?.clients_metrics?.channels?.whatsapp.received_and_bought_with_received_product?.count]),
          ...(!this.isChannelActivated('push') ? [] : [this.metrics?.clients_metrics?.channels?.push.received_and_bought_with_received_product?.count]),
        ],
      ]
    },
    quickActionsProps() {
      return [
        {
          key: `clients-received-campaigns`,
          quickActionGroupId: `clients-received-campaigns`,
          shortDescription: 'Clientes que receberam disparos',
          longDescription: `Clientes que receberam disparos na campanha ${this.campaign.title}`,
          segmentationGroup: () =>
            criteriaGroup([
              campaignReceivedCriteria([this.campaign._id], this.dateCriteriaFilter.formattedInitialDate, this.dateCriteriaFilter.formattedFinalDate),
            ]),
        },
        {
          key: 'received_and_purchased_any_campaign_maria_campaign',
          quickActionGroupId: 'received_and_purchased_any_campaign_maria_campaign',
          shortDescription: `Clientes que receberam campanhas e compraram`,
          longDescription: `Clientes que compraram na campanha ${this.campaign.title}`,
          segmentationGroup: criteriaGroup(
            campaignReceivedAndPurchasedAtUnitsOnFixedPeriodCriteria(
              [this.campaign._id],
              this.dateCriteriaFilter.formattedInitialDate,
              this.dateCriteriaFilter.formattedFinalDate,
              this.unitsConfiguration
            )
          ),
        },
        {
          key: `clients-received-and-bought-with-offer-products`,
          quickActionGroupId: `clients-received-and-bought-with-offer-products`,
          shortDescription: 'Clientes que receberam campanhas e compraram os produtos da(s) oferta(s)',
          longDescription: `Clientes que compraram produtos divulgados na campanha ${this.campaign.title}`,
          segmentationGroup: () =>
            criteriaGroup(
              campaignReceivedAndPurchasedOfferProductAtUnitsCriteria(
                [this.campaign._id],
                this.dateCriteriaFilter.formattedInitialDate,
                this.dateCriteriaFilter.formattedFinalDate,
                this.unitsConfiguration
              )
            ),
        },
        {
          key: `clients-received-and-bought-with-received-product`,
          quickActionGroupId: `clients-received-and-bought-with-received-product`,
          shortDescription: 'Clientes que receberam campanhas e compraram  o produto que recebeu',
          longDescription: `Clientes que receberam e compraram o produto que receberam via variável na mensagem da campanha ${this.campaign.title}`,
          segmentationGroup: () =>
            criteriaGroup(
              campaignReceivedAndPurchasedVariableProductAtStoreCriteria(
                [this.campaign._id],
                this.dateCriteriaFilter.formattedInitialDate,
                this.dateCriteriaFilter.formattedFinalDate,
                this.unitsConfiguration
              )
            ),
        },
      ]
    },
  },
}
</script>
